<!--
/*
 * Компонент [OnboardingOverlay] — пошаговый интерактивный тур для новых пользователей VPN.
 */
-->
<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { useEventListener, useTimeoutFn, useWindowSize } from '@vueuse/core'
import { useUserStore } from '@/modules/user/stores/userStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import type { IOnboardingStep } from './types/IOnboardingStep'
import { useI18n } from 'vue-i18n'

const emit = defineEmits<{
  (e: 'complete'): void
}>()

const { t } = useI18n()

const steps = computed<IOnboardingStep[]>(() => [
  {
    target: '[data-onboarding="copy-link"]',
    icon: 'ri-link',
    title: t('onboarding.step1Title'),
    description: t('onboarding.step1Desc'),
    tooltipPosition: 'bottom',
  },
  {
    target: '[data-onboarding="instructions"]',
    icon: 'ri-book-open-line',
    title: t('onboarding.step2Title'),
    description: t('onboarding.step2Desc'),
    tooltipPosition: 'bottom',
  },
  {
    target: '[data-onboarding="profile"]',
    icon: 'ri-user-line',
    title: t('onboarding.step3Title'),
    description: t('onboarding.step3Desc'),
    tooltipPosition: 'top',
  },
  {
    target: '[data-onboarding="support"]',
    icon: 'ri-chat-3-line',
    title: t('onboarding.step4Title'),
    description: t('onboarding.step4Desc'),
    tooltipPosition: 'top',
  },
])

const currentStep = ref(0)
const isReady = ref(false)
const isTransitioning = ref(false)

const transitionTimer = useTimeoutFn(
  () => {
    isTransitioning.value = false
  },
  450,
  { immediate: false },
)

const initTimer = useTimeoutFn(
  () => {
    updateSpotlightPosition()
    isReady.value = true
  },
  300,
  { immediate: false },
)

const { width: windowWidth, height: windowHeight } = useWindowSize()
const spotlightRect = ref({ top: 0, left: 0, width: 0, height: 0 })

const step = computed(() => steps.value[currentStep.value])
const isLastStep = computed(() => currentStep.value === steps.value.length - 1)

const spotlightStyle = computed(() => {
  const padding = 8
  return {
    top: `${spotlightRect.value.top - padding}px`,
    left: `${spotlightRect.value.left - padding}px`,
    width: `${spotlightRect.value.width + padding * 2}px`,
    height: `${spotlightRect.value.height + padding * 2}px`,
  }
})

const tooltipStyle = computed(() => {
  const rect = spotlightRect.value
  const padding = 8
  const screenMargin = 16

  if (step.value.tooltipPosition === 'top') {
    const bottomOffset = windowHeight.value - rect.top + padding + 12
    return {
      bottom: `${bottomOffset}px`,
      left: '16px',
      right: '16px',
      maxHeight: `${windowHeight.value - bottomOffset - screenMargin}px`,
    }
  }
  const topOffset = rect.top + rect.height + padding + 12
  return {
    top: `${topOffset}px`,
    left: '16px',
    right: '16px',
    maxHeight: `${windowHeight.value - topOffset - screenMargin}px`,
  }
})

function updateSpotlightPosition() {
  const targetEl = document.querySelector(step.value.target) as HTMLElement | null

  if (targetEl) {
    const rect = targetEl.getBoundingClientRect()
    spotlightRect.value = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    }
  } else {
    spotlightRect.value = {
      top: windowHeight.value - 80,
      left: windowWidth.value / 2 - 30,
      width: 60,
      height: 50,
    }
  }
}

async function nextStep() {
  if (isTransitioning.value) return

  if (isLastStep.value) {
    completeOnboarding()
    return
  }

  isTransitioning.value = true
  currentStep.value++

  await nextTick()
  updateSpotlightPosition()
  transitionTimer.start()
}

function skipOnboarding() {
  completeOnboarding()
}

const userStore = useUserStore()
const feedbackStore = useFeedbackStore()

async function completeOnboarding() {
  const result = await userStore.completeOnboarding()
  if (!result.ok) {
    feedbackStore.showToast({
      type: 'error',
      message: result.error || t('errors.onboardingFailed'),
    })
  }
  emit('complete')
}

function onResize() {
  updateSpotlightPosition()
}

onMounted(async () => {
  await nextTick()
  initTimer.start()
})

useEventListener(window, 'resize', onResize)
</script>

<template>
  <Transition name="onboarding-fade">
    <div
      v-if="isReady"
      class="onboarding-overlay"
    >
      <svg class="onboarding-overlay__mask">
        <defs>
          <mask id="spotlight-mask">
            <rect
              width="100%"
              height="100%"
              fill="white"
            />
            <rect
              :x="spotlightRect.left - 8"
              :y="spotlightRect.top - 8"
              :width="spotlightRect.width + 16"
              :height="spotlightRect.height + 16"
              rx="12"
              ry="12"
              fill="black"
              class="onboarding-overlay__cutout"
            />
          </mask>
        </defs>

        <rect
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.65)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      <div
        class="onboarding-spotlight"
        :style="spotlightStyle"
      >
        <div class="onboarding-spotlight__pulse" />
      </div>

      <div
        class="onboarding-tooltip"
        :style="tooltipStyle"
        :class="{ 'onboarding-tooltip--entering': isTransitioning }"
      >
        <v-card class="onboarding-tooltip__card" elevation="12">
          <div class="d-flex flex-column ga-3 pa-4">
            <div class="d-flex align-center ga-3">
              <div class="onboarding-tooltip__icon-wrap">
                <v-icon
                  size="24"
                  class="onboarding-tooltip__icon"
                >
                  {{ step.icon }}
                </v-icon>
              </div>
              <div class="text-h6 font-weight-bold">
                {{ step.title }}
              </div>
            </div>

            <div class="text-body-2 text-medium-emphasis">
              {{ step.description }}
            </div>

            <div class="d-flex justify-center ga-2">
              <div
                v-for="(_, index) in steps"
                :key="index"
                class="onboarding-dot"
                :class="{
                  'onboarding-dot--active': index === currentStep,
                  'onboarding-dot--done': index < currentStep,
                }"
              />
            </div>

            <div class="d-flex ga-3">
              <v-btn
                variant="text"
                class="flex-grow-1"
                @click="skipOnboarding"
              >
                {{ t('onboarding.skip') }}
              </v-btn>

              <v-btn
                color="primary"
                class="flex-grow-1"
                :loading="isTransitioning"
                @click="nextStep"
              >
                {{ isLastStep ? t('onboarding.done') : t('onboarding.next') }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.onboarding-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: auto;
}

.onboarding-overlay__mask {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.onboarding-overlay__cutout {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.onboarding-spotlight {
  position: fixed;
  border-radius: 12px;
  border: 2px solid rgb(var(--v-theme-primary));
  pointer-events: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.onboarding-spotlight__pulse {
  position: absolute;
  inset: -4px;
  border-radius: 14px;
  border: 2px solid rgb(var(--v-theme-primary));
  opacity: 0;
  animation: spotlight-pulse 2s ease-in-out infinite;
}

@keyframes spotlight-pulse {
  0%, 100% {
    opacity: 0;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.05);
  }
}

.onboarding-tooltip {
  position: fixed;
  z-index: 10000;
  max-width: calc(var(--app-max-width) - 32px);
  margin-inline: auto;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  animation: tooltip-appear 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.onboarding-tooltip--entering {
  opacity: 0.3;
  transform: scale(0.96);
}

.onboarding-tooltip__card {
  border-radius: 16px !important;
  overflow: visible;
  backdrop-filter: blur(12px);
}

.onboarding-tooltip__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgb(var(--v-theme-primary));
  flex-shrink: 0;
}

.onboarding-tooltip__icon {
  color: rgb(var(--v-theme-on-primary)) !important;
}

.onboarding-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(var(--v-theme-on-surface), 0.2);
  transition: all 0.3s ease;
}

.onboarding-dot--active {
  width: 24px;
  border-radius: 4px;
  background: rgb(var(--v-theme-primary));
}

.onboarding-dot--done {
  background: rgba(var(--v-theme-primary), 0.5);
}

@keyframes tooltip-appear {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.onboarding-fade-enter-active {
  transition: opacity 0.5s ease;
}

.onboarding-fade-leave-active {
  transition: opacity 0.3s ease;
}

.onboarding-fade-enter-from,
.onboarding-fade-leave-to {
  opacity: 0;
}
</style>
